import { useRef } from "react"
import { Box, Chip, Typography } from "@mui/material"
import type { GridColDef, GridSortModel } from "@mui/x-data-grid-pro"
import { DataGrid } from "@core/components/dataGrid/dataGrid"
import { GRID_PAGE_SIZE_OPTIONS } from "@core/constants"
import { MAX_LIST_LIMIT } from "@core/hooks/useUrlListState"
import { formatNullableDateAndTime } from "@core/utils/date"
import {
  BOT_MONITOR_SORT_FIELDS,
  BOT_MONITOR_STATUS_LABELS,
} from "../../constants/botMonitor"
import type { BotMonitorRow } from "../../interfaces/botMonitor"

const dateColumn = (
  field: keyof BotMonitorRow,
  headerName: string,
): GridColDef<BotMonitorRow> => ({
  field,
  headerName,
  width: 190,
  renderCell: ({ value }) => (
    <span title={value ?? undefined}>{formatNullableDateAndTime(value)}</span>
  ),
})
const baseColumns: GridColDef<BotMonitorRow>[] = [
  {
    field: "username",
    headerName: "Username",
    width: 220,
    renderCell: ({ row }) => (
      <span title={`${row.username} (botId: ${row.botId})`}>
        {row.username}
      </span>
    ),
  },
  {
    field: "status",
    headerName: "Статус",
    width: 190,
    renderCell: ({ row }) => (
      <Chip
        size="small"
        label={BOT_MONITOR_STATUS_LABELS[row.status]}
        color={
          row.status === "active"
            ? "success"
            : row.status === "frozen"
              ? "error"
              : "default"
        }
      />
    ),
  },
  dateColumn("createdAt", "Создание бота"),
  dateColumn("blockedAt", "Обнаружен бан"),
  dateColumn("lastCheckAt", "Последняя попытка"),
  dateColumn("lastSuccessfulCheckAt", "Последний успех"),
  {
    field: "lastCheckOutcome",
    headerName: "Исход попытки",
    width: 160,
    renderCell: ({ value }) =>
      value === "ok" ? "Успешно" : value === "error" ? "Ошибка" : "—",
  },
  {
    field: "lastCheckError",
    headerName: "Ошибка попытки",
    width: 200,
    renderCell: ({ value }) => (
      <span title={value ?? undefined}>{value ?? "—"}</span>
    ),
  },
]
const columns = baseColumns.map((column) => ({
  ...column,
  sortable: BOT_MONITOR_SORT_FIELDS.some((field) => field === column.field),
  filterable: false,
  editable: false,
}))
const getRowId = (row: BotMonitorRow) => row.botId
const NoOverlay = () => null
const pageSizeOptions = GRID_PAGE_SIZE_OPTIONS.filter(
  (size) => size <= MAX_LIST_LIMIT,
)
interface Props {
  rows: BotMonitorRow[]
  total?: number
  limit: number
  offset: number
  loading?: boolean
  failed?: boolean
  sortModel: GridSortModel
  onSortChange: (model: GridSortModel) => void
  onOffsetChange: (offset: number) => void
  onLimitChange: (limit: number) => void
}
export const BotMonitorTable = ({
  rows,
  total,
  limit,
  offset,
  loading = false,
  failed = false,
  sortModel,
  onSortChange,
  onOffsetChange,
  onLimitChange,
}: Props) => {
  // Retain pagination metadata during requests, unless it would constrain a
  // restored URL page using another query's smaller total. -1 means unknown.
  const lastTotal = useRef<number>()
  if (total !== undefined) lastTotal.current = total
  const knownTotal = total ?? lastTotal.current
  // An out-of-range response stays an explicit empty URL page; only user
  // navigation changes its offset. Do not let MUI silently redirect/refetch it.
  const rowCount =
    knownTotal === undefined || (offset > 0 && offset >= knownTotal)
      ? -1
      : knownTotal
  const page = Math.floor(offset / limit)
  const sizes = [...new Set([...pageSizeOptions, limit])]
    .filter(
      (size) => Number.isInteger(size) && size >= 1 && size <= MAX_LIST_LIMIT,
    )
    .sort((left, right) => left - right)
  return (
    <Box>
      {loading && <Typography role="status">Загрузка мониторинга…</Typography>}
      {!loading && !failed && total !== undefined && rows.length === 0 && (
        <Typography>
          {total === 0
            ? "По текущим фильтрам боты не найдены"
            : `На этой странице нет строк; всего: ${total}`}
        </Typography>
      )}
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          height: {
            xs: "clamp(360px, 60vh, 640px)",
            sm: "clamp(240px, 60vh, 640px)",
          },
        }}
      >
        <DataGrid<BotMonitorRow>
          autoHeight={false}
          disableVirtualization={false}
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          loading={loading}
          pagination
          paginationMode="server"
          sortingMode="server"
          rowCount={rowCount}
          paginationModel={{
            page,
            pageSize: limit,
          }}
          onPaginationModelChange={({ pageSize }) => {
            if (pageSize !== limit) onLimitChange(pageSize)
          }}
          pageSizeOptions={sizes}
          sortModel={sortModel}
          onSortModelChange={onSortChange}
          disableMultipleColumnsSorting
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
          slots={{ noRowsOverlay: NoOverlay }}
          slotProps={{
            pagination: {
              // MUI X v6's footer clamps unknown counts to page -1. Keep the
              // native footer on the URL page without inventing a numeric total.
              count: rowCount,
              page,
              // User navigation comes directly from the native footer, not
              // from grid events that also include automatic page corrections.
              onPageChange: (_event, nextPage) =>
                onOffsetChange(nextPage * limit),
              ...(rowCount === -1
                ? {
                    labelDisplayedRows: () =>
                      total === undefined
                        ? "Всего: неизвестно"
                        : `Всего: ${total}`,
                  }
                : {}),
              backIconButtonProps: {
                disabled: loading || failed || page === 0,
              },
              nextIconButtonProps: {
                disabled:
                  loading ||
                  failed ||
                  rowCount === -1 ||
                  offset + limit >= rowCount,
              },
            },
          }}
          sx={{
            "&&.MuiDataGrid-root": { height: "100%" },
            ".MuiDataGrid-row:hover": { cursor: "default" },
          }}
        />
      </Box>
    </Box>
  )
}
